import {
  JEZIKI,
  MODUL_PROJEKTI,
  POGLEDI_PROJEKTOV,
  type Jezik,
  type Podjetje,
  type PorociloNaVoljo,
  type Uporabnik,
} from '@koda/shared';
import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { Link, NavLink, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { api } from '../lib/api';
import { useBesedilaProjektov } from '../lib/besedila-projektov';
import { SPREMEMBA_PODJETIJ } from '../lib/dogodki';
import { useBesedila } from '../lib/jezik';

/** Glavni zavihek menija s svojimi poročili (podzavihki). */
export interface SkupinaZavihkov {
  koda: string;
  naziv: string;
  porocila: PorociloNaVoljo[];
}

/**
 * Poročila razvrsti v glavne zavihke (docs/ARHITEKTURA.md, 6.6) v vrstnem
 * redu, kot jih vrne strežnik. Poročilo brez skupine je svoj glavni zavihek.
 */
export function skupineZavihkov(porocila: readonly PorociloNaVoljo[]): SkupinaZavihkov[] {
  const skupine: SkupinaZavihkov[] = [];
  for (const p of porocila) {
    const koda = p.skupina?.koda ?? `porocilo:${p.vrsta}`;
    const obstojeca = skupine.find((s) => s.koda === koda);
    if (obstojeca !== undefined) obstojeca.porocila.push(p);
    else skupine.push({ koda, naziv: p.skupina?.naziv ?? p.naziv, porocila: [p] });
  }
  return skupine;
}

/** Inicialke za krogec: »Teo Velik« → »TV«; brez imena prvi dve črki e-pošte. */
export function inicialke(u: Pick<Uporabnik, 'ime' | 'email'>): string {
  const deli = (u.ime ?? '').trim().split(/\s+/u).filter((d) => d !== '');
  if (deli.length >= 2) return `${deli[0]!.charAt(0)}${deli[deli.length - 1]!.charAt(0)}`.toLocaleUpperCase();
  if (deli.length === 1) return deli[0]!.slice(0, 2).toLocaleUpperCase();
  return u.email.slice(0, 2).toLocaleUpperCase();
}

interface Lastnosti {
  uporabnik: Uporabnik;
  naOdjavo: () => void;
  /** Poklicano po preklopu jezika, da se aplikacija prebere znova. */
  naJezik: () => void;
}

/**
 * Glavni meni v eni vrstici (zasnova »Kazalniki – dashboard«), enak na vseh
 * straneh in za stranko in skrbnika: levo znamka in zavihki poročil
 * odprtega podjetja, desno izbira podjetja in krogec z inicialkami (račun,
 * jezik, odjava). Skrbnik ima nad njim še ozko vrstico skrbniških strani.
 *
 * Katera poročila ima podjetje, pove paket (`porocilaNaVoljo`), ne koda.
 * Izbira podjetja nadomesti drobtine; stranka z enim podjetjem vidi samo
 * njegovo ime.
 */
export function Glava({ uporabnik, naOdjavo, naJezik }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const navigiraj = useNavigate();
  const { search } = useLocation();
  const vPodjetju = useMatch('/podjetje/:id/*');
  const vPorocilu = useMatch('/podjetje/:id/porocilo/:vrsta');
  // Strani modula Projekti so svoj glavni zavihek s podzavihki (15.5).
  const vProjektih = useMatch('/podjetje/:id/projekti/:pogled');
  const bp = useBesedilaProjektov();
  const idPodjetja = vPodjetju?.params.id ?? null;
  const [podjetja, setPodjetja] = useState<Podjetje[]>([]);
  const [porocila, setPorocila] = useState<PorociloNaVoljo[]>([]);
  const [moduli, setModuli] = useState<string[]>([]);
  // Poveča se ob vsaki spremembi podjetij (naziv, novo podjetje, arhiv,
  // paket in moduli): seznama se takrat prebereta znova, da meni ne kaže
  // zastarelega naziva ali zavihkov.
  const [razlicica, setRazlicica] = useState(0);
  useEffect(() => {
    const obSpremembi = (): void => {
      setRazlicica((r) => r + 1);
    };
    window.addEventListener(SPREMEMBA_PODJETIJ, obSpremembi);
    return () => {
      window.removeEventListener(SPREMEMBA_PODJETIJ, obSpremembi);
    };
  }, []);

  // Seznam podjetij za izbiro (samo tista, do katerih ima uporabnik dostop –
  // to zagotovi strežnik).
  useEffect(() => {
    let prekinjeno = false;
    api
      .podjetja()
      .then((p) => {
        if (!prekinjeno) setPodjetja(p);
      })
      .catch(() => {
        if (!prekinjeno) setPodjetja([]);
      });
    return () => {
      prekinjeno = true;
    };
  }, [razlicica]);

  // Zavihki poročil odprtega podjetja (paket podjetja, v jeziku uporabnika).
  useEffect(() => {
    if (idPodjetja === null) {
      setPorocila([]);
      setModuli([]);
      return undefined;
    }
    let prekinjeno = false;
    api
      .porocilaNaVoljo(idPodjetja, uporabnik.jezik)
      .then((p) => {
        if (!prekinjeno) setPorocila(p);
      })
      .catch(() => {
        if (!prekinjeno) setPorocila([]);
      });
    // Vklopljeni moduli: zavihek Projekti samo pri podjetju z modulom.
    Promise.resolve()
      .then(() => api.moduli(idPodjetja))
      .then((m) => {
        if (!prekinjeno) setModuli(m);
      })
      .catch(() => {
        if (!prekinjeno) setModuli([]);
      });
    return () => {
      prekinjeno = true;
    };
  }, [idPodjetja, uporabnik.jezik, razlicica]);
  const podjetje = podjetja.find((p) => p.id === idPodjetja) ?? null;
  const [odprtMeni, setOdprtMeni] = useState(false);
  const [menjamGeslo, setMenjamGeslo] = useState(false);
  const [staro, setStaro] = useState('');
  const [novo, setNovo] = useState('');
  const [sporocilo, setSporocilo] = useState<string | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const ovoj = useRef<HTMLDivElement>(null);

  // Meni se zapre ob kliku drugam in ob Escape, kot je pri menijih običajno.
  useEffect(() => {
    if (!odprtMeni) return undefined;
    const obKliku = (dogodek: MouseEvent): void => {
      if (!ovoj.current?.contains(dogodek.target as Node)) setOdprtMeni(false);
    };
    const obTipki = (dogodek: KeyboardEvent): void => {
      if (dogodek.key === 'Escape') setOdprtMeni(false);
    };
    document.addEventListener('mousedown', obKliku);
    document.addEventListener('keydown', obTipki);
    return () => {
      document.removeEventListener('mousedown', obKliku);
      document.removeEventListener('keydown', obTipki);
    };
  }, [odprtMeni]);

  const zamenjaj = (dogodek: FormEvent): void => {
    dogodek.preventDefault();
    setNapaka(null);
    setSporocilo(null);
    api
      .spremeniGeslo(staro, novo)
      .then(() => {
        setSporocilo(b.gesloZamenjano);
        setStaro('');
        setNovo('');
        setMenjamGeslo(false);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : b.gesloNiUspelo);
      });
  };

  const razred = ({ isActive }: { isActive: boolean }): string =>
    isActive ? 'meni-povezava izbrana' : 'meni-povezava';

  /**
   * Preklop jezika.
   *
   * Izbira se shrani pri uporabniku, zato velja tudi na drugi napravi; ko je
   * shranjena, se aplikacija prebere znova, da so nazivi in porocila v novem
   * jeziku (docs/ARHITEKTURA.md, 7).
   */
  const preklopiJezik = (jezik: Jezik): void => {
    if (jezik === uporabnik.jezik) return;
    api
      .nastaviJezik(jezik)
      .then(naJezik)
      .catch(() => {
        /* jezik ni kriticna nastavitev; ob napaki ostane star */
      });
  };

  /**
   * Glavni zavihki (6.6). Dokler strežnik poročil ne razvrsti (nobeno nima
   * skupine), ostane en sam niz zavihkov kot prej.
   */
  const razvrscena = porocila.some((p) => p.skupina !== null && p.skupina !== undefined);
  const skupine = skupineZavihkov(porocila);
  const izbranaSkupina = skupine.find((s) => s.porocila.some((p) => p.vrsta === vPorocilu?.params.vrsta));
  const imaProjekte = moduli.includes(MODUL_PROJEKTI);
  // Zavihki so vidni v porocilih in na straneh Projektov; Projekti so zadnji.
  const vZavihkih = vPorocilu !== null || (vProjektih !== null && imaProjekte);
  const zavihekProjektov = imaProjekte && idPodjetja !== null && (
    <Link
      to={`/podjetje/${idPodjetja}/projekti/pregled`}
      className={vProjektih !== null ? 'zavihek-porocila izbran' : 'zavihek-porocila'}
      aria-current={vProjektih !== null ? 'true' : undefined}
    >
      {b.projekti}
    </Link>
  );

  return (
    <header className="glava">
      {uporabnik.jeAdmin && (
        <nav className="skrbniski-meni" aria-label={b.skrbnik}>
          <NavLink to="/" end className={razred}>
            {b.podjetja}
          </NavLink>
          <NavLink to="/uporabniki" className={razred}>
            {b.uporabniki}
          </NavLink>
          <NavLink to="/posta" className={razred}>
            {b.posiljanja}
          </NavLink>
          <NavLink to="/arhiv" className={razred}>
            {b.arhiv}
          </NavLink>
          <NavLink to="/stanje-baze" className={razred}>
            {b.stanjeBaze}
          </NavLink>
          <NavLink to="/nastavitve/kazalniki" className={razred}>
            {b.nastavitve}
          </NavLink>
          {/* Skrbniška stran odprtega podjetja (uvoz, nastavitve, uporabniki). */}
          {idPodjetja !== null && (
            <NavLink to={`/podjetje/${idPodjetja}`} end className={({ isActive }) => `${razred({ isActive })} desno`}>
              {podjetje === null ? b.uvozInNastavitve : `${podjetje.naziv}: ${b.uvozInNastavitve.toLocaleLowerCase()}`}
            </NavLink>
          )}
        </nav>
      )}
      <div className="glava-vrstica">
        <Link to="/" className="znamka" aria-label={b.znamka}>
          {b.znamkaPrvi} <span className="znamka-poudarek">{b.znamkaDrugi}</span>
        </Link>
        {vZavihkih && (porocila.length > 0 || imaProjekte) && (razvrscena || porocila.length === 0) && (
          /* Glavni zavihki: klik odpre prvo poročilo skupine. */
          <nav className="zavihki-porocil" aria-label={b.glavniZavihki}>
            {skupine.map((s) => (
              <Link
                key={s.koda}
                to={`/podjetje/${idPodjetja ?? ''}/porocilo/${s.porocila[0]!.vrsta}${search}`}
                className={s === izbranaSkupina ? 'zavihek-porocila izbran' : 'zavihek-porocila'}
                aria-current={s === izbranaSkupina ? 'true' : undefined}
              >
                {s.naziv}
              </Link>
            ))}
            {zavihekProjektov}
          </nav>
        )}
        {vZavihkih && porocila.length > 0 && !razvrscena && (
          <nav className="zavihki-porocil" aria-label={b.porocila}>
            {porocila.map((p) => (
              <Link
                key={p.vrsta}
                to={`/podjetje/${idPodjetja ?? ''}/porocilo/${p.vrsta}${search}`}
                className={p.vrsta === vPorocilu?.params.vrsta ? 'zavihek-porocila izbran' : 'zavihek-porocila'}
                aria-current={p.vrsta === vPorocilu?.params.vrsta ? 'page' : undefined}
              >
                {p.naziv}
              </Link>
            ))}
            {zavihekProjektov}
          </nav>
        )}

        <div className="glava-desno" ref={ovoj}>
          {idPodjetja !== null &&
            (podjetja.length > 1 || uporabnik.jeAdmin ? (
              <label className="izbira-podjetja">
                <span className="skrito">{b.podjetje}</span>
                <select
                  value={idPodjetja}
                  onChange={(d) => {
                    // Novo podjetje ima lahko druga leta: odpre se njegov pregled.
                    navigiraj(d.target.value === '' ? '/' : `/podjetje/${d.target.value}`);
                  }}
                >
                  {podjetje === null && <option value={idPodjetja}>{b.podjetje}</option>}
                  {podjetja.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.naziv}
                    </option>
                  ))}
                  <option value="">{b.vsaPodjetja}</option>
                </select>
              </label>
            ) : (
              podjetje !== null && <span className="izbira-podjetja samo-ime">{podjetje.naziv}</span>
            ))}
          <button
            type="button"
            className="krogec"
            aria-expanded={odprtMeni}
            aria-label={`${uporabnik.ime ?? uporabnik.email}${uporabnik.jeAdmin ? ` (${b.skrbnik})` : ''}`}
            title={uporabnik.ime ?? uporabnik.email}
            onClick={() => {
              setOdprtMeni((prej) => !prej);
            }}
          >
            {inicialke(uporabnik)}
          </button>
          {odprtMeni && (
            <div className="uporabnik-meni">
              <div className="meni-naslov">
                <strong>{uporabnik.ime ?? uporabnik.email}</strong>
                {uporabnik.ime !== null && <span className="drobno">{uporabnik.email}</span>}
                {uporabnik.jeAdmin && <span className="oznaka">{b.skrbnik}</span>}
              </div>
              <div className="jezik" role="group" aria-label={b.jezikOznaka}>
                {JEZIKI.map((j) => (
                  <button
                    key={j}
                    type="button"
                    className={j === uporabnik.jezik ? 'jezik-gumb izbran' : 'jezik-gumb'}
                    aria-pressed={j === uporabnik.jezik}
                    onClick={() => {
                      preklopiJezik(j);
                    }}
                  >
                    {j.toUpperCase()}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  setMenjamGeslo((prej) => !prej);
                  setOdprtMeni(false);
                  setSporocilo(null);
                  setNapaka(null);
                }}
              >
                {b.zamenjajGeslo}
              </button>
              <button type="button" onClick={naOdjavo}>
                {b.odjava}
              </button>
            </div>
          )}
        </div>
      </div>
      {/*
        Podzavihki izbranega glavnega zavihka (6.6). Skupina z enim samim
        poročilom jih nima – glavni zavihek odpre poročilo neposredno.
      */}
      {vProjektih !== null && imaProjekte && idPodjetja !== null && (
        <nav className="podzavihki-porocil" aria-label={b.projekti}>
          {POGLEDI_PROJEKTOV.map((pogled) => (
            <Link
              key={pogled}
              to={`/podjetje/${idPodjetja}/projekti/${pogled}`}
              className={pogled === vProjektih.params.pogled ? 'podzavihek izbran' : 'podzavihek'}
              aria-current={pogled === vProjektih.params.pogled ? 'page' : undefined}
            >
              {bp.pogledi[pogled]}
            </Link>
          ))}
        </nav>
      )}
      {vPorocilu !== null && razvrscena && izbranaSkupina !== undefined && izbranaSkupina.porocila.length > 1 && (
        <nav className="podzavihki-porocil" aria-label={b.porocila}>
          {izbranaSkupina.porocila.map((p) => (
            <Link
              key={p.vrsta}
              to={`/podjetje/${idPodjetja ?? ''}/porocilo/${p.vrsta}${search}`}
              className={p.vrsta === vPorocilu.params.vrsta ? 'podzavihek izbran' : 'podzavihek'}
              aria-current={p.vrsta === vPorocilu.params.vrsta ? 'page' : undefined}
            >
              {p.naziv}
            </Link>
          ))}
        </nav>
      )}

      {sporocilo !== null && <p className="uspeh-besedilo drobno">{sporocilo}</p>}

      {menjamGeslo && (
        <form onSubmit={zamenjaj} className="obrazec menjava-gesla">
          <label>
            {b.staroGeslo}
            <input
              type="password"
              value={staro}
              autoComplete="current-password"
              onChange={(d) => {
                setStaro(d.target.value);
              }}
              required
            />
          </label>
          <label>
            {b.novoGeslo}
            <input
              type="password"
              value={novo}
              autoComplete="new-password"
              minLength={10}
              onChange={(d) => {
                setNovo(d.target.value);
              }}
              required
            />
          </label>
          {napaka !== null && <p className="napaka">{napaka}</p>}
          <button type="submit">{b.zamenjaj}</button>
        </form>
      )}
    </header>
  );
}
